/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { ComboBox, Select } from "@eds/react";
import type { Option } from "@eds/react";
import { useCallback } from "react";
import type { FilterOption } from "../core/Directory.types";

export type DirectoryComboBoxProps = {
	options: FilterOption[];
	values: string[];
	onChange: (values: string[]) => void;
	/**
	 * Accessible label for the combobox. Required so the rendered control
	 * always has an a11y name (WCAG 4.1.2). Use the FilterDef's `label`.
	 */
	label: string;
	placeholder?: string;
	ariaLabel?: string;
	/**
	 * aria-label for the "remove this selected option" button on each chip inside the
	 * ComboBox. EDS requires this when `multiple=true`. Defaults to "Remove".
	 */
	removeLabel?: string;
	/**
	 * - `chips` (default) — searchable EDS ComboBox; selected values render as chips inside the field.
	 * - `count` — non-searchable dropdown whose field reads "{placeholder} (N)"; pair it with
	 *   `DirectoryActiveFilters` so the selected values stay visible and removable (Figma 698:14246).
	 */
	display?: "chips" | "count";
};

/**
 * Multi-value combobox. Selected values surface as chips inside the EDS
 * ComboBox component (or as a count in `display="count"` mode) and can be removed
 * individually. Engine-level semantics (OR/AND) are controlled by the FilterDef's `matchMode`.
 */
export function DirectoryComboBox({
	options,
	values,
	onChange,
	label,
	ariaLabel,
	placeholder,
	removeLabel = "Remove",
	display = "chips",
}: DirectoryComboBoxProps): React.JSX.Element {
	if (process.env.NODE_ENV !== "production" && !(label?.trim() || ariaLabel?.trim())) {
		console.warn(
			"[DirectoryComboBox] An empty `label` and `ariaLabel` was passed; the rendered combobox will have no accessible name. Supply a meaningful label or set a localized fallback at the FilterDef.",
		);
	}

	const edsOptions: Option<string>[] = options.map((o) => ({
		label: o.label,
		value: o.value,
		disabled: o.disabled,
	}));

	const selected = edsOptions.filter((o) => values.includes(o.value));

	const handleChange = useCallback(
		(next: Option<string>[]) => {
			onChange(next.map((o) => o.value));
		},
		[onChange],
	);

	if (display === "count") {
		const name = placeholder || label || ariaLabel || "";
		return (
			<Select<string, true, false, false, true>
				label={label || undefined}
				placeholder={placeholder}
				options={edsOptions}
				inputProps={{ "aria-label": ariaLabel }}
				multiple
				singleLine
				value={selected}
				onChange={handleChange}
				// EDS calls this for an empty selection too — `null` falls back to the placeholder.
				renderLabel={(opts) => (opts.length > 0 ? `${name} (${opts.length})` : null)}
			/>
		);
	}

	return (
		<ComboBox<string, true>
			label={label}
			placeholder={placeholder}
			options={edsOptions}
			inputProps={{ "aria-label": ariaLabel }}
			multiple
			value={selected}
			onChange={handleChange}
			removeLabel={removeLabel}
		/>
	);
}
